import React, { useCallback } from 'react';
import Table, { TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/Table';
import { flexRender, Table as ReactTable } from '@tanstack/react-table';
import cn from '@/utils/cn';
import useCellValidation from '../hooks/useCellValidation';
import {
  IHolisticRubricDescription,
} from '../../useQuestionRubric';
import styles from './HolisticRubric.module.scss';
import { isEmptyContent } from '../AddRubricModal.utils';

const CELL_CLASSES = 'tw-relative tw-border-solid tw-border-[1px] tw-border-med-grey tw-text-left';
const HIGHLIGHT_CLASSES = 'tw-border-2 tw-border-red';

export type HolisticRubricProps = {
  classes?: {
    cell?: string,
    container?: string,
    header?: string,
    score_column?: string,
  };
  hasHeaders?: boolean;
  table: ReactTable<IHolisticRubricDescription>;
  validationAttempt?: number;
};

const HolisticRubric = ({
  classes = {},
  hasHeaders = true,
  table,
  validationAttempt = 0,
} : HolisticRubricProps) => {
  const { acknowledgedCells, handleCellClick, getCellKey } = useCellValidation({ validationAttempt });

  const isCellEmpty = useCallback((rowData: IHolisticRubricDescription, columnId: string) => {
    if (columnId === 'score') return false;
    if (columnId === 'description_en') {
      return !rowData.description_en || isEmptyContent(rowData.description_en);
    }

    return false;
  }, []);

  const shouldHighlightCell = (rowData: IHolisticRubricDescription, columnId: string, rowIndex: number) => {
    if (validationAttempt === 0) return false;

    const cellKey = getCellKey(rowIndex, columnId);
    const isAcknowledged = acknowledgedCells.has(cellKey);
    const isEmpty = isCellEmpty(rowData, columnId);

    return isEmpty && !isAcknowledged;
  };

  return (
    <Table className={cn('tw-w-full', classes.container)}>
      {hasHeaders && (
        <TableHeader className="tw-bg-light-grey">
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <TableCell
                  key={header.id}
                  header
                  className={cn(CELL_CLASSES, classes.header, 'tw-font-semibold')}
                  style={{ width: header.column.columnDef.meta?.width }}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHeader>
      )}
      <TableBody>
        {table.getRowModel().rows.map(row => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell, i) => {
              const isHighlighted = shouldHighlightCell(row.original, cell.column.id, row.index);

              return (
                <TableCell
                  key={cell.id}
                  className={cn(
                    styles.holisticDescriptionCell,
                    CELL_CLASSES,
                    classes.cell,
                    isHighlighted && HIGHLIGHT_CLASSES,
                    i === 0 && classes.score_column,
                    {
                      'tw-bg-light-grey': i === 0,
                      'tw-bg-white': i !== 0,
                    },
                  )}
                  style={{ width: cell.column.columnDef.meta?.width }}
                  onClick={() => {
                    if (cell.column.id === 'score') return;
                    handleCellClick(row.index, cell.column.id);
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default HolisticRubric;
